import React from 'react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { useLanguage } from '@/contexts/LanguageContext';
import { getServerDetailPath } from '@/utils/i18nRouting';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { useFavoriteServer, useIsFavorite } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { useSteamServerStatus } from '@/hooks/useSteamServerStatus';
import { ServerCardHeader } from '@/components/ServerCardHeader';
import { ServerCardContent } from '@/components/ServerCardContent';
import { ServerCardFooter } from '@/components/ServerCardFooter';
import { ServerCardStats } from '@/components/ServerCardStats';

interface ServerCardProps {
  rank: number;
  name: string;
  description: string;
  ip: string;
  version: string;
  category: string;
  players: { online: number; max: number };
  votes: number;
  tags: string[];
  isOnline: boolean;
  serverId: string;
  serverSlug?: string;
  isSponsored?: boolean;
  sponsorTier?: string;
  customColor?: string;
  bannerImageUrl?: string;
  logoImageUrl?: string;
  prestigeExpiresAt?: string | null;
  isCompetitiveMode?: boolean;
  competitiveVotes?: number;
  competitiveRank?: number;
  rankChange?: number;
}

export const ServerCard: React.FC<ServerCardProps> = ({
  rank, name, description, ip, version: _version, category, players: _players, votes, tags,
  isOnline: _isOnline, serverId, serverSlug, isSponsored = false, sponsorTier,
  customColor, bannerImageUrl, logoImageUrl, prestigeExpiresAt,
  isCompetitiveMode = false, competitiveVotes, competitiveRank, rankChange,
}) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const favoriteMutation = useFavoriteServer();
  const isFavorite = useIsFavorite(serverId);

  const { data: gameData } = useQuery({
    queryKey: ['game-type', category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games').select('is_steam, logo_url, backdrop_url').eq('name', category).single();
      if (error) return null;
      return data;
    },
    enabled: !!category,
  });

  const isSteamGame = gameData?.is_steam === true;
  const gameFallbackImage = gameData?.logo_url || gameData?.backdrop_url || undefined;
  const { data: steamStatus } = useSteamServerStatus(serverId, isSteamGame && ip && ip !== '0.0.0.0');

  const { data: serverData } = useQuery({
    queryKey: ['server-admin-data', serverId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('servers')
        .select('id, name, is_sponsored, prestige_expires_at, created_at, updated_at, owner_id, custom_color')
        .eq('id', serverId).single();
      if (error) return null;
      return data;
    },
    enabled: !!user,
  });

  const currentPrestigeActive = isSponsored && prestigeExpiresAt && new Date(prestigeExpiresAt) > new Date();
  const isGold = currentPrestigeActive && (sponsorTier === 'gold' || sponsorTier === 'premium' || sponsorTier === 'PRESTIGE GOLD');

  const getCardClasses = () => {
    const base = 'retro-card group relative overflow-hidden transition-all duration-300 hover:scale-[1.01] hover:shadow-neon cursor-pointer';
    return isGold
      ? `${base} retro-card-premium h-auto`
      : `${base} retro-card-standard h-auto`;
  };

  const getCustomStyles = () => {
    if (isGold && customColor) {
      return { borderColor: customColor, boxShadow: `0 0 12px ${customColor}40, inset 0 0 12px ${customColor}20` };
    }
    return {};
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    favoriteMutation.mutate({ serverId, isFavorite: !isFavorite });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('.favorite-button') || target.closest('button') ||
      target.closest('.admin-panel-trigger') || target.closest('[role="menuitem"]') ||
      target.closest('[data-radix-collection-item]') || target.closest('[data-radix-dialog-content]') ||
      target.closest('[data-radix-dialog-overlay]') || target.closest('[role="dialog"]') ||
      target.closest('.dialog-content')
    ) return;
    e.preventDefault();
    window.location.href = getServerDetailPath(language, serverSlug ? { slug: serverSlug } : { id: serverId });
  };

  const getPlayerCount = () => {
    if (isSteamGame && ip && ip !== '0.0.0.0' && steamStatus?.online && steamStatus.players !== undefined) {
      return `${steamStatus.players}/${steamStatus.maxPlayers}`;
    }
    return null;
  };

  return (
    <div className="block cursor-pointer" onClick={handleCardClick}>
      <Card className={getCardClasses()} style={getCustomStyles()}>
        {/* Bannière arrière-plan */}
        <div className="absolute inset-0 banner-container">
          {currentPrestigeActive && bannerImageUrl ? (
            <OptimizedImage
              src={bannerImageUrl}
              alt={`Bannière serveur ${name} - Serveur ${category} privé`}
              imageType="banner"
              width={400}
              height={200}
              loading="lazy"
              className="banner-image"
              fallback={<div className="banner-fallback" />}
            />
          ) : (
            <div className="banner-fallback" />
          )}
          <div className="banner-overlay" />
        </div>

        {/* Layout en flux normal (pas de positionnement absolu) */}
        <div className="relative z-10 flex flex-col">
          {/* Ligne 1 : Header stabilisé pour Firefox */}
          <div className="flex items-start gap-3 px-3 pt-2 pb-1 sm:pb-2">
            <div className="min-w-0 flex-1">
              <ServerCardHeader
                rank={isCompetitiveMode ? competitiveRank || 0 : rank}
                isSponsored={currentPrestigeActive}
                sponsorTier={sponsorTier}
                isGold={isGold}
                serverData={serverData ? { ...serverData, custom_color: customColor } : null}
                userId={user?.id}
                apiToken={undefined}
                isCompetitiveMode={isCompetitiveMode}
                competitiveVotes={competitiveVotes}
                rankChange={rankChange}
              />
            </div>
            <div className="flex-shrink-0 pt-0.5">
              <ServerCardStats serverId={serverId} fallbackVotes={votes} playerCount={getPlayerCount()} />
            </div>
          </div>

          {/* Ligne 2 : Contenu (logo + nom + description) */}
          <ServerCardContent
            name={name}
            description={description}
            logoImageUrl={logoImageUrl}
            gameFallbackImage={gameFallbackImage}
            gameName={category}
            serverId={serverId}
            fallbackVotes={votes}
            playerCount={getPlayerCount()}
            isPrestige={currentPrestigeActive}
          />

          {/* Ligne 3 : Footer (tags, IP, favoris) */}
          <div className="px-3 pb-2">
            <ServerCardFooter
              ip={ip}
              user={user}
              isFavorite={isFavorite}
              favoriteMutation={favoriteMutation}
              serverId={serverId}
              onFavoriteClick={handleFavoriteClick}
              category={category}
              tags={tags}
              isServerPrestige={currentPrestigeActive}
            />
          </div>
        </div>

        {/* Effet shimmer Prestige */}
        {isGold && (
          <div
            className="absolute inset-0 animate-shimmer-subtle pointer-events-none z-0"
            style={customColor ? { background: `linear-gradient(90deg, transparent, ${customColor}10, transparent)` } : {}}
          />
        )}
      </Card>
    </div>
  );
};
